import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDecideLessonEarning, useLessonEarnings } from '../../api/tutor-applications';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { ApiError } from '../../lib/api';
import { formatDateTime, formatMoney } from '../../lib/format';

/**
 * Tutor-reported no-shows, waiting for a decision.
 *
 * Only the tutor can report a no-show ([07](docs/specs/07-sessions.md)), so
 * the earning sits in `pending_review` until somebody checks the call log
 * against the report. Confirming pays it; voiding does not. Neither touches
 * the amounts — the split was settled when the lesson ended, and only the
 * status moves.
 */
export function NoShowReview({ count }: { count: number }) {
  const earnings = useLessonEarnings({ status: 'pending_review', limit: 20 });
  const decide = useDecideLessonEarning();
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(sessionId: string, decision: 'confirm' | 'void') {
    setActing(sessionId);
    setError(null);
    try {
      await decide.mutateAsync({ sessionId, decision });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setActing(null);
    }
  }

  return (
    <section className="panel overflow-hidden">
      <header className="flex items-baseline justify-between gap-3 border-b border-rule px-4 py-3">
        <h2 className="text-sm font-semibold text-ink">
          No-shows awaiting a decision
          {count > 0 && <span className="ms-2 tabular-nums text-ink-3">{count}</span>}
        </h2>
        <p className="text-xs text-ink-3">
          Check the call log against the tutor&rsquo;s report before confirming.
        </p>
      </header>

      {error && (
        <p role="alert" className="border-b border-rule px-4 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      {earnings.isError ? (
        <ErrorState
          message={earnings.error instanceof ApiError ? earnings.error.message : undefined}
          onRetry={() => void earnings.refetch()}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tutor</th>
                <th>Lesson</th>
                <th>Would earn</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {earnings.isLoading ? (
                <SkeletonRows rows={3} cols={4} />
              ) : earnings.data && earnings.data.data.length > 0 ? (
                earnings.data.data.map((e) => (
                  <tr key={e.sessionId}>
                    <td className="text-sm text-ink">{e.tutorName}</td>
                    <td className="text-xs text-ink-2">
                      <Link to={`/sessions/${e.sessionId}`} className="link">
                        {e.sessionStartedAt ? formatDateTime(e.sessionStartedAt) : 'Open lesson'}
                      </Link>
                      {e.studentName && (
                        <span className="mt-0.5 block text-ink-3">{e.studentName}</span>
                      )}
                    </td>
                    <td className="text-sm tabular-nums text-ink">
                      {formatMoney(e.netCents, e.currency)}
                      <span className="mt-0.5 block text-xs text-ink-3">
                        of {formatMoney(e.grossCents, e.currency)}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={acting !== null}
                          onClick={() => void run(e.sessionId, 'confirm')}
                        >
                          {acting === e.sessionId ? '…' : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={acting !== null}
                          onClick={() => void run(e.sessionId, 'void')}
                        >
                          Void
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      title="Nothing waiting"
                      description="No tutor has reported a no-show that still needs a decision."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
